import { useState, useCallback, useEffect, useRef } from "react";
import type { Channel } from "@hybrid-iptv/types";
import { useDialogFocus } from "@hybrid-iptv/ui";

interface ChannelBoxProps {
  channels: Channel[];
  onZapTo: (channel: Channel) => void;
}

/** Compact channel-number entry. Click the box to open an onscreen keypad;
 *  tuning is by channel number (remote-style), not by search. */
export function ChannelBox({ channels, onZapTo }: ChannelBoxProps) {
  const [digits, setDigits] = useState("");
  const [error, setError] = useState(false);
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const keypadRef = useRef<HTMLDivElement>(null);

  // TV-remote Back/Escape closes the keypad; Tab is trapped inside it.
  useDialogFocus({ open, onClose: () => setOpen(false), rootRef: keypadRef });

  // Close the keypad on outside click
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  const commit = useCallback(
    (num: number) => {
      const match = channels.find((ch) => ch.number === num);
      if (match) {
        onZapTo(match);
        setOpen(false);
      }
      setDigits("");
      setError(!match);
    },
    [channels, onZapTo],
  );

  const pushDigit = useCallback(
    (d: string) => {
      setError(false);
      const next = digits + d;
      setDigits(next);
      const num = parseInt(next, 10);
      const exact = channels.find((ch) => ch.number === num);
      if (exact) {
        onZapTo(exact);
        setOpen(false);
        setDigits("");
      } else if (next.length >= 3) {
        setError(true);
      }
    },
    [digits, channels, onZapTo],
  );

  const clearDigits = useCallback(() => {
    setDigits("");
    setError(false);
  }, []);

  return (
    <div className="epg-channel-box" ref={rootRef}>
      <button
        type="button"
        className={`epg-channel-box__display${error ? " epg-channel-box__display--error" : ""}`}
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-label="Channel number entry"
        title="Channel number entry"
      >
        <span className="epg-channel-box__label">CH</span>
        <span className="epg-channel-box__digits">{digits || "—"}</span>
      </button>

      {open && (
        <div ref={keypadRef} className="epg-keypad" role="dialog" aria-label="Channel keypad" tabIndex={-1}>
          <div className="epg-keypad__row">
            {["1", "2", "3"].map((d) => (
              <button key={d} className="epg-keypad__key" onClick={() => pushDigit(d)}>{d}</button>
            ))}
          </div>
          <div className="epg-keypad__row">
            {["4", "5", "6"].map((d) => (
              <button key={d} className="epg-keypad__key" onClick={() => pushDigit(d)}>{d}</button>
            ))}
          </div>
          <div className="epg-keypad__row">
            {["7", "8", "9"].map((d) => (
              <button key={d} className="epg-keypad__key" onClick={() => pushDigit(d)}>{d}</button>
            ))}
          </div>
          <div className="epg-keypad__row">
            <button className="epg-keypad__key epg-keypad__key--clear" onClick={clearDigits} aria-label="Clear">⌫</button>
            <button className="epg-keypad__key" onClick={() => pushDigit("0")}>0</button>
            <button className="epg-keypad__key epg-keypad__key--enter" onClick={() => commit(parseInt(digits, 10))} aria-label="Tune">✓</button>
          </div>
        </div>
      )}
    </div>
  );
}
